/**
 * SmartFit / Online Sizing – automatic size selection
 * Theme: Enterprise 1.6.2 (bike-mailorder.com)
 *
 * Two jobs:
 *   1. window.confirmSize(event)  – the widget's `onselect` callback. Takes the
 *      recommended variant and selects it in the theme's variant picker.
 *   2. on:variant:change listener – keeps the widget in sync when the customer
 *      changes the size themselves.
 *
 * Why this differs from SmartFit's documented Shopify example: that example is
 * written for Dawn. Enterprise has a different variant picker (<variant-picker>
 * with .option-selector fieldsets) and no Dawn pub/sub, so the selector logic
 * and the change event are both different. See README.md.
 */
(function () {
  'use strict';

  var WIDGET_ID = 'onlinesizing-widget';

  function log() {
    if (!window.SMARTFIT_DEBUG) return;
    console.log.apply(console, ['[smartfit]'].concat([].slice.call(arguments)));
  }

  function getWidget() {
    return document.getElementById(WIDGET_ID);
  }

  /** The variant picker belonging to the main product, not a quick-add drawer. */
  function getPicker() {
    var widget = getWidget();
    var scope = widget && widget.closest('.js-product');
    return (scope || document).querySelector('variant-picker');
  }

  /** Enterprise embeds the product JSON inside <variant-picker>. Reuse it. */
  function getProduct(picker) {
    var el = picker && picker.querySelector('[type="application/json"]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent).product;
    } catch (err) {
      log('could not parse product JSON', err);
      return null;
    }
  }

  /**
   * The widget's payload code is the variant id we passed in as `variant-id`.
   * We also accept a barcode/GTIN so the callback keeps working if the widget
   * ever echoes back `global-id` instead.
   */
  function findVariant(product, code) {
    if (!product || code === null || code === undefined) return null;
    var needle = String(code).trim();
    if (!needle) return null;

    var byId = product.variants.filter(function (v) {
      return String(v.id) === needle;
    })[0];
    if (byId) return byId;

    return product.variants.filter(function (v) {
      return v.barcode && String(v.barcode).trim() === needle;
    })[0] || null;
  }

  /**
   * Select one option value inside one .option-selector.
   *
   * We compare values in JS rather than building an attribute selector, because
   * real size values in this catalogue contain quotes, brackets and commas
   * (S (27.5"), M - 29 Zoll, 52 cm - S/M) which are painful to escape safely.
   */
  function selectOption(selector, value) {
    var isDropdown = selector.dataset.selectorType === 'dropdown';
    var options = selector.querySelectorAll('.js-option');

    for (var i = 0; i < options.length; i++) {
      var option = options[i];
      var optionValue = isDropdown ? option.dataset.value : option.value;
      if (optionValue !== value) continue;

      if (isDropdown) return selectDropdownOption(selector, option);

      if (option.checked) return true; // already correct, nothing to do
      option.checked = true;
      // <variant-picker> listens for a bubbling change event and reads
      // evt.target, so it has to be dispatched from the input itself.
      option.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    log('no option matching', value, 'in', selector);
    return false;
  }

  /**
   * Dropdown fallback. custom-select only binds its click handler while the
   * listbox is open, so open it first. If the theme rendered a native <select>
   * companion, drive that instead - it is more reliable.
   */
  function selectDropdownOption(selector, option) {
    var customSelect = selector.querySelector('custom-select') || selector;
    var native = customSelect.id
      ? document.getElementById(customSelect.id + '-native')
      : null;

    if (native) {
      native.value = option.dataset.value;
      native.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    var button = selector.querySelector('.custom-select__btn');
    if (button) button.click();
    option.click();
    return true;
  }

  /** The widget's onselect callback. Must be global - it is referenced by name. */
  window.confirmSize = function confirmSize(event) {
    var code = event && event.detail ? event.detail.code : null;
    log('confirmSize', code, event);

    var picker = getPicker();
    if (!picker) return log('no variant-picker on page');

    var product = getProduct(picker);
    var variant = findVariant(product, code);
    if (!variant) {
      // Also happens when the widget recommends an alternative bike - that is
      // a different product, so there is nothing to select here.
      return log('no variant on this product for code', code);
    }

    var selectors = picker.querySelectorAll('.option-selector');
    variant.options.forEach(function (value, index) {
      var selector = selectors[index];
      if (selector) selectOption(selector, value);
    });

    log('selected variant', variant.id, variant.options);
  };

  /** Keep the widget pointed at whatever variant is currently selected. */
  document.addEventListener('on:variant:change', function (event) {
    var variant = event.detail && event.detail.variant;
    var widget = getWidget();
    if (!widget || !variant) return;

    widget.setAttribute('variant-id', variant.id);
    if (variant.barcode) widget.setAttribute('global-id', variant.barcode);
    if (typeof variant.price === 'number') {
      widget.setAttribute('price', (variant.price / 100).toFixed(2));
    }
    log('widget synced to variant', variant.id);
  });
})();
